// The catalog (PLAN-CLIENT-SPLIT §2.4): the rules' tables in the contract's words.
//
// **Built once and shared** — nothing here reads state, so there is nothing to
// memoise on; every `Game` hands out the same frozen list. The card entry is
// `handModel`'s minus the instance, built from the same three places
// (`CARD_LIST`, `rarityOfCard`, `cardGlyph`) so the hand and the codex cannot
// disagree about what a card is.

import type { Catalog, CatalogAlliance, CatalogCard, CatalogEntry, CatalogMap, CatalogScenario } from "src/api";
import { LEVELS } from "src/engine/ai/difficulty";
import { PLAY_PROFILES } from "src/engine/ai/profiles";
import { ALL_SCENARIOS, ALLIANCE_LAYOUTS, ARMIES, MOST_SIDES, sidesOf } from "src/engine/ai/scenarios";
import { CARD_LIST, type Card } from "src/engine/rules/campaign/cards";
import { BOARD_MAPS } from "src/engine/rules/maps";
import { cardGlyph, kitRarity } from "src/engine/session/hand";
import { RARITY_NAME, rarityOfCard } from "src/engine/session/rarity";

/** `Card.spendsTurn`, named: absent is free, `true` is a move, `"turn"` is the turn. */
const costOf = (card: Card): CatalogCard["cost"] =>
  card.spendsTurn === "turn" ? "turn" : card.spendsTurn ? "move" : "free";

export const catalogCard = (card: Card): CatalogCard => {
  const rarity = rarityOfCard(card.id);
  return {
    id: card.id,
    name: card.name,
    short: card.short,
    text: card.text,
    targeted: card.targeted,
    rarity: kitRarity(rarity),
    rarityName: RARITY_NAME[rarity],
    glyph: cardGlyph(card.id),
    reusable: card.reusable === true,
    cost: costOf(card)
  };
};

const CARDS: readonly CatalogCard[] = Object.freeze(CARD_LIST.map(catalogCard));

const entry = ({ id, name, detail }: { id: string; name: string; detail: string }): CatalogEntry => ({
  id,
  name,
  detail
});

const DIFFICULTIES: readonly CatalogEntry[] = Object.freeze(LEVELS.map(entry));
const SCENARIOS: readonly CatalogScenario[] = Object.freeze(
  ALL_SCENARIOS.map((scenario) => ({ ...entry(scenario), sides: sidesOf(scenario) }))
);
const ARMY_STYLES: readonly CatalogEntry[] = Object.freeze(ARMIES.map(entry));
const PROFILES: readonly CatalogEntry[] = Object.freeze(PLAY_PROFILES.map(entry));
const MAPS: readonly CatalogMap[] = Object.freeze(
  BOARD_MAPS.map((map) => ({ id: map.id, name: map.name, detail: map.blurb, generated: map.generate !== undefined }))
);
const ALLIANCES: readonly CatalogAlliance[] = Object.freeze(
  ALLIANCE_LAYOUTS.map((layout) => ({ ...entry(layout), sides: layout.sides }))
);

export const catalog: Catalog = {
  cards: () => CARDS,
  difficulties: () => DIFFICULTIES,
  scenarios: () => SCENARIOS,
  armies: () => ARMY_STYLES,
  profiles: () => PROFILES,
  maps: () => MAPS,
  alliances: () => ALLIANCES,
  mostSides: () => MOST_SIDES
};
